
import React, {useState, useEffect} from 'react';
import{ useSelector } from "react-redux";
import CustomLoaderSmall from "../../../src/components/Layout/CustomLoaderSmall";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import {useGetAllUsersQuery, useFetchEmployeeByProjectIdListQuery } from "../../../src/features/userApiSlice";
import {useGetAllProjectsQuery} from "../../../src/features/projectApiSlice";
import NavigationSideBar from "../../components/Layout/Graph & Tables/NavigationSideBar";
import DashboardHeader from "../../components/Layout/DashBoardHeader";
import {useNavigate, useParams} from "react-router-dom";
import Toast, {toast} from "react-toastify";
import {useAssignEmployeeToProjectMutation, useRemoveEmployeeFromProjectMutation, useListAllProjectEmployeesQuery} from "../../../src/features/projectApiSlice";

import {
    PanelBottomOpen,
    LucideRotateCcw,
    XCircleIcon,
    ChevronDownIcon,
    Search, Trash2Icon,
} from "lucide-react";
import {useSwipeable} from "react-swipeable";

export function AssignEmployeeToProject() {
    const {userInfo} = useSelector((state: any) => state).auth;

    const navigate = useNavigate();
    const {id} = useParams();
    const [selectCheckMark, setSelectCheckMark] = useState<boolean>(false);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
    const [selectProjectCheckMark, setSelectProjectCheckMark] = useState<boolean>(false);
    const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

    const [showFilters, setShowFilters] = useState(false);
    const [projectNameFilter, setProjectNameFilter] = useState("");
    const [userNameFilter, setUserNameFilter] = useState("");

    const {data: users, isLoading, refetch} = useGetAllUsersQuery(userInfo?.id);
    // @ts-ignore
    const {data: projects, isLoading: projectsLoading} = useGetAllProjectsQuery();
    const [assignEmployeeToProject] = useAssignEmployeeToProjectMutation();
    const [removeEmployeeFromProject] = useRemoveEmployeeFromProjectMutation();
    // @ts-ignore
    const {data: assignedEmployees, isLoading: assignedEmployeesLoading, refetch:refetchAssignedEmployees} = useListAllProjectEmployeesQuery();

    const filteredAssignedEmployees = assignedEmployees?.filter(
        (item: any) => {
            const projectName = item.projectName?.toLowerCase() ?? "";
            const userName = item.userName?.toLowerCase() ?? "";

            return (
                projectName.includes(projectNameFilter.trim().toLowerCase()) &&
                userName.includes(userNameFilter.trim().toLowerCase())
            );
        }
    );

    const handleEmployeeSelection = (employeeId: string) => {
        setSelectedEmployeeId((currentId) => currentId === employeeId ? null : employeeId);
        setSelectCheckMark(true)
    };

    const handleProjectSelection = (projectId: string) => {
        setSelectedProjectId((currentId) => currentId === projectId ? null : projectId);
        setSelectProjectCheckMark(true)
    }

    const handleProjectResetSelection = () => {
        setSelectProjectCheckMark(false);
        setSelectedProjectId(null);
    }

    const handleResetSelection = () => {
        setSelectedEmployeeId(null);
        setSelectCheckMark(false);
    }

    const onRemoveEmployeeFromProject =  async (
        userId: string,
        projectId: string
    ) => {
        if (!userId|| !projectId) {
            toast.error("Project ID and User ID is required");
            return;
        }

        if (!userInfo) {
            toast.error("Authentication is required");
            return;
        }
        if (window.confirm("Are you sure you want to delete this product now?")) {
            try {
                await removeEmployeeFromProject({
                    userId: userId,
                    projectId: projectId,
                }).unwrap();


                // Refresh the assigned employees list
                await refetchAssignedEmployees();

                // Optional: clear selections
                if (selectedEmployeeId === userId) {
                    setSelectedEmployeeId(null);
                    setSelectCheckMark(false);
                }

                if (selectedProjectId === projectId) {
                    setSelectedProjectId(null);
                    setSelectProjectCheckMark(false);
                }

                toast.success("Successfully removed Employee from project");


            } catch (error) {
                toast.error("Problem with deleting assigned employee");
            }
        }
        }


    const onAssignEmployeeToProjectHandler = async () => {
        if (!selectedEmployeeId || !selectedProjectId) {
            toast.error("Project ID and User ID is required");
            return;
        }

        if (!userInfo) {
            toast.error("Authentication is required");
            return;
        }

        try {
            await assignEmployeeToProject({
                userId: selectedEmployeeId.toString(),
                projectId: selectedProjectId.toString(),
            }).unwrap()

            if (userInfo.Id === selectedEmployeeId && selectedProjectId === id) {
                toast.error("Project has already been assigned");
                return;
            }

            // clear the selection after successful assignment
            setSelectedEmployeeId(null);
            setSelectedProjectId(null);
            setSelectCheckMark(false);
            setSelectProjectCheckMark(false);

            // Refresh employee data
            await refetch();

            toast.success("Successfully Assigned Employee To Project");

            navigate("/management");


        } catch (error) {

            toast.error(error.message);
        }
    };

    {/* Pagination */}
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(6);

    const totalItems = filteredAssignedEmployees?.length;

    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;

    const currentAssignedEmployees = filteredAssignedEmployees?.slice(
        indexOfFirstItem,
        indexOfLastItem
    );

// Reset to first page when filters change
   useEffect(() => {
        setCurrentPage(1);
    }, [projectNameFilter, userNameFilter]);

// Keep current page valid when records change
   useEffect(() => {
        setCurrentPage((page) =>
            Math.min(page, Math.max(totalPages, 1))
        );
    }, [totalPages]);


    return (
        <>
            <Helmet>
                <title>Assign Employee To Project</title>

                <meta name={"description"} content={"Assign Employee To Project"}/>

            </Helmet>

            {isLoading || projectsLoading || assignedEmployeesLoading ? (
                <CustomLoaderSmall/>
            ) : (
                <motion.div
                    initial={{
                        opacity: 0,
                        y: 20,
                    }}
                    animate={{
                        opacity: 1,
                        y: 0,
                    }}
                    transition={{
                        delay: 0.2,
                    }}
                >
                    <div className={"bg-white text-black flex min-h-dvh"}>

                        <div className={"hidden lg:block"}>
                            <NavigationSideBar/>
                        </div>

                        <div className={"flex-1 flex min-w-0 flex-col"}>
                            <DashboardHeader projectId={id}/>


                            {/* REGISTER BUTTON - TOP RIGHT */}
                            <div className="mb-2 flex justify-between my-2 px-3">

                                <button type={"button"}
                                        className={" hover:bg-gray-300 p-1 rounded-lg transition duration-500"}
                                        onClick={() => navigate(`/management`)}>
                                    <XCircleIcon size={36} color={"gray"}/>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => {
                                        onAssignEmployeeToProjectHandler();
                                    }}
                                    disabled={!selectCheckMark || !selectProjectCheckMark}
                                    className=" py-2

                                                    inline-flex
                                                    min-h-[45px]
                                                    items-center
                                                    justify-center
                                                    gap-2
                                                    rounded-xl
                                                    bg-gray-500
                                                    px-5
                                                    text-sm
                                                    font-semibold
                                                    text-white
                                                    transition
                                                    hover:bg-blue-900
                                                    disabled:cursor-not-allowed
                                                    disabled:opacity-40
        "
                                >
                                    <PanelBottomOpen size={17}/>
                                    Assign User To A Project
                                </button>
                            </div>


                            <main
                                className={"w-full max-w-[1600px] mx-auto px-2 sm:px-6 md:px-8 lg:px-10" +
                                    "py-5 sm:py-6 md:py-8 pb-[env(safe-area-inset-bottom)]"}
                            >


                                <section
                                    className={"px-1 py-2 sm:px-4 sm:py-3 lg:px-6 lg:py-3 container mx-auto w-full"}>
                                    <div
                                        className={"flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6"}>

                                        <div className="
                                    bg-gray-700
                                    border
                                    border-gray-600
                                    rounded-lg
                                    overflow-hidden
                                    mb-3
                                "
                                        >
                                            <div className={"p-4 border-b border-gray-600"}>
                                                <div
                                                    className=""
                                                >
                                                    <div className={"flex items-center justify-between gap-x-2"}>
                                                        <h2 className={"text-lg font-semibold text-white"}>
                                                            Employee Information
                                                        </h2>

                                                        <button
                                                            type="button"
                                                            onClick={handleResetSelection}
                                                            disabled={!selectCheckMark}
                                                            className="
                                                                flex items-center gap-2
                                                                rounded-lg
                                                                border border-gray-200
                                                                px-3 py-2
                                                                text-sm font-medium
                                                                text-gray-900
                                                                transition
                                                                hover:bg-white
                                                                disabled:cursor-not-allowed
                                                                disabled:opacity-40
                                                            "
                                                        >
                                                            <LucideRotateCcw size={18} color={"pink"}/>

                                                            Reset Selection
                                                        </button>

                                                    </div>


                                                </div>
                                            </div>

                                            <div className={"overflow-x-auto"}>
                                                <table className={"w-full text-left min-w-[1090px] shadow-lg"}>
                                                    <thead className={"bg-gray-900/50"}>
                                                    <tr>
                                                        <th
                                                            className="
                                                        p-4
                                                        text-xs
                                                        font-semibold
                                                        text-gray-400
                                                        uppercase
                                                    "
                                                        >
                                                            First Name
                                                        </th>

                                                        <th
                                                            className="
                                                        p-4
                                                        text-xs
                                                        font-semibold
                                                        text-gray-400
                                                        uppercase
                                                    "
                                                        >
                                                            Last Name
                                                        </th>


                                                        <th
                                                            className="
                                                        p-4
                                                        text-xs
                                                        font-semibold
                                                        text-gray-400
                                                        uppercase
                                                    "
                                                        >
                                                            Employee Number
                                                        </th>


                                                        <th
                                                            className="
                                                        p-4
                                                        text-xs
                                                        font-semibold
                                                        text-gray-400
                                                        uppercase
                                                    "
                                                        >
                                                            Email
                                                        </th>


                                                        <th
                                                            className="
                                                        p-4
                                                        text-xs
                                                        font-semibold
                                                        text-gray-400
                                                        uppercase
                                                    "
                                                        >
                                                            Phone Number
                                                        </th>

                                                    </tr>
                                                    </thead>

                                                    {users?.map((user) => (
                                                        <motion.tr
                                                            key={
                                                                user.id
                                                            }
                                                            initial={{
                                                                opacity: 0,
                                                            }}
                                                            animate={{
                                                                opacity: 1,
                                                            }}

                                                            className="
                                                            border-t
                                                            border-gray-800
                                                            hover:bg-gray-900/60
                                                            cursor-pointer
                                                            transition-colors
                                                        "
                                                        >
                                                            {/* Employee */}
                                                            <td className={"p-3"}>
                                                                <div className={"flex items-center gap-3"}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedEmployeeId === user.id}
                                                                        onChange={() => handleEmployeeSelection(user.id)}
                                                                        className={"h-5 w-5 cursor-pointer font-light rounded-lg accent-blue-300"}
                                                                    />

                                                                    <p className={"font-medium text-gray-200"}>
                                                                        {user.firstName}
                                                                    </p>
                                                                </div>
                                                            </td>

                                                            <td className={"p-3"}>
                                                                <div className={"flex items-center gap-2"}>

                                                                    <div className={"items-center"}>
                                                                        <p className={"text-gray-200 font-medium"}>
                                                                            {user.lastName}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            <td className={"p-4 text-gray-300"}>
                                                                <p className={"text-gray-200 font-medium"}>
                                                                    # {user.employeeNumber}
                                                                </p>
                                                            </td>

                                                            <td className={"p-4 text-gray-300"}>
                                                                <p className={"text-gray-200 font-medium"}>
                                                                    {user.email}
                                                                </p>
                                                            </td>

                                                            <td className={"p-4 text-gray-300"}>
                                                                <p className={"text-gray-200 font-medium"}>
                                                                    {user.phoneNumber}
                                                                </p>
                                                            </td>


                                                        </motion.tr>
                                                    ))}
                                                </table>
                                            </div>

                                        </div>


                                    </div>
                                </section>

                                <section
                                    className={"px-1 py-2 sm:px-4 sm:py-3 lg:px-6 lg:py-3 container mx-auto w-full"}>
                                    <div
                                        className={"flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6"}>

                                        <div className="
                                    bg-gray-700
                                    border
                                    border-gray-600
                                    rounded-lg
                                    overflow-hidden
                                    mb-3
                                "
                                        >
                                            <div className={"p-4 border-b border-gray-600"}>
                                                <div
                                                    className=""
                                                >
                                                    <div className={"flex items-center justify-between gap-x-2"}>
                                                        <h2 className={"text-lg font-semibold text-white"}>
                                                            Projects
                                                        </h2>

                                                        <button
                                                            type="button"
                                                            onClick={handleProjectResetSelection}
                                                            disabled={!selectProjectCheckMark}
                                                            className="
                                                                flex items-center gap-2
                                                                rounded-lg
                                                                border border-gray-200
                                                                px-3 py-2
                                                                text-sm font-medium
                                                                text-gray-900
                                                                transition
                                                                hover:bg-white
                                                                disabled:cursor-not-allowed
                                                                disabled:opacity-40
                                                            "
                                                        >
                                                            <LucideRotateCcw size={18} color={"red"}/>

                                                            Reset Selection
                                                        </button>

                                                    </div>


                                                </div>
                                            </div>

                                            <div className={"overflow-x-auto"}>
                                                <table className={"w-full text-left min-w-[1090px] shadow-lg"}>
                                                    <thead className={"bg-gray-900/50"}>
                                                    <tr>
                                                        <th
                                                            className="
                                                        p-4
                                                        text-xs
                                                        font-semibold
                                                        text-gray-400
                                                        uppercase
                                                    "
                                                        >
                                                            Project Name
                                                        </th>

                                                        <th
                                                            className="
                                                        p-4
                                                        text-xs
                                                        font-semibold
                                                        text-gray-400
                                                        uppercase
                                                    "
                                                        >
                                                            # Number
                                                        </th>


                                                        <th
                                                            className="
                                                        p-4
                                                        text-xs
                                                        font-semibold
                                                        text-gray-400
                                                        uppercase
                                                    "
                                                        >
                                                            Location
                                                        </th>


                                                        <th
                                                            className="
                                                        p-4
                                                        text-xs
                                                        font-semibold
                                                        text-gray-400
                                                        uppercase
                                                    "
                                                        >
                                                            Client
                                                        </th>


                                                        <th
                                                            className="
                                                        p-4
                                                        text-xs
                                                        font-semibold
                                                        text-gray-400
                                                        uppercase
                                                    "
                                                        >
                                                            Description
                                                        </th>

                                                    </tr>
                                                    </thead>

                                                    {projects?.map((site) => (
                                                        <motion.tr
                                                            key={
                                                                site.id
                                                            }
                                                            initial={{
                                                                opacity: 0,
                                                            }}
                                                            animate={{
                                                                opacity: 1,
                                                            }}

                                                            className="
                                                            border-t
                                                            border-gray-800
                                                            hover:bg-gray-900/60
                                                            cursor-pointer
                                                            transition-colors
                                                        "
                                                        >
                                                            {/* Employee */}
                                                            <td className={"p-3"}>
                                                                <div className={"flex items-center gap-3"}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedProjectId === site.id}
                                                                        onChange={() => handleProjectSelection(site.id)}
                                                                        className={"h-5 w-5 cursor-pointer font-light rounded-lg accent-blue-300"}
                                                                    />

                                                                    <p className={"font-medium text-gray-200"}>
                                                                        {site.projectName}
                                                                    </p>
                                                                </div>
                                                            </td>

                                                            <td className={"p-3"}>
                                                                <div className={"flex items-center gap-2"}>

                                                                    <div className={"items-center"}>
                                                                        <p className={"text-gray-200 font-medium"}>
                                                                            # {site.projectNumber}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            <td className={"p-4 text-gray-300"}>
                                                                <p className={"text-gray-200 font-medium"}>
                                                                    {site.location}
                                                                </p>
                                                            </td>

                                                            <td className={"p-4 text-gray-300"}>
                                                                <p className={"text-gray-200 font-medium"}>
                                                                    {site.contractor}
                                                                </p>
                                                            </td>

                                                            <td className={"p-4 text-gray-300"}>
                                                                <p className={"text-gray-200 font-medium"}>
                                                                    {site.description}
                                                                </p>
                                                            </td>


                                                        </motion.tr>
                                                    ))}
                                                </table>

                                                {/*<div className="mt-4 rounded-lg bg-gray-100 p-4 text-sm">*/}
                                                {/*    <h3 className="mb-2 font-bold">Selection Debug</h3>*/}

                                                {/*    <p>*/}
                                                {/*        Selected Employee ID:{" "}*/}
                                                {/*        <span className="font-semibold">*/}
                                                {/*            {selectedEmployeeId ?? "No employee selected"}*/}
                                                {/*        </span>*/}
                                                {/*    </p>*/}

                                                {/*    <p>*/}
                                                {/*        Selected Project ID:{" "}*/}
                                                {/*        <span className="font-semibold">*/}
                                                {/*        {selectedProjectId ?? "No project selected"}*/}
                                                {/*    </span>*/}
                                                {/*    </p>*/}
                                                {/*</div>*/}

                                            </div>

                                        </div>

                                    </div>
                                </section>

                                <section
                                    className={"px-1 py-2 sm:px-4 sm:py-3 lg:px-6 lg:py-3 container mx-auto w-full"}>
                                    <div
                                        className={"flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6"}>

                                        <div className="
                                    bg-gray-700
                                    border
                                    border-gray-600
                                    rounded-lg
                                    overflow-hidden
                                    mb-3
                                "
                                        >
                                            <div className="p-4 border-b border-gray-600">
                                                <div className="flex items-center justify-between">

                                                    <h2 className="text-lg font-semibold text-white">
                                                        Assigned Employees
                                                    </h2>

                                                    <button
                                                        type="button"
                                                        onClick={() => setShowFilters((prev) => !prev)}
                                                        aria-label="Toggle employee filters"
                                                        aria-expanded={showFilters}
                                                        className="rounded-full p-2 text-white
                 transition hover:bg-gray-600"
                                                    >
                                                        <ChevronDownIcon
                                                            size={22}
                                                            className={`transition-transform duration-300 ${
                                                                showFilters ? "rotate-180" : "rotate-0"
                                                            }`}
                                                        />
                                                    </button>

                                                </div>

                                                {showFilters && (
                                                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">

                                                        <div>
                                                            <label className="mb-1 block text-sm text-gray-200">
                                                                Project Name
                                                            </label>

                                                            <input
                                                                type="text"
                                                                value={projectNameFilter}
                                                                onChange={(e) => setProjectNameFilter(e.target.value)}
                                                                placeholder="Search project..."
                                                                className="w-full rounded-lg border border-gray-500
                                                                 bg-gray-800 p-3 text-white outline-none
                                                                 focus:border-blue-400"
                                                            />
                                                        </div>

                                                        <div>
                                                            <label className="mb-1 block text-sm text-gray-200">
                                                                User Name
                                                            </label>

                                                            <input
                                                                type="text"
                                                                value={userNameFilter}
                                                                onChange={(e) => setUserNameFilter(e.target.value)}
                                                                placeholder="Search employee..."
                                                                className="w-full rounded-lg border border-gray-500
                                                                 bg-gray-800 p-3 text-white outline-none
                                                                 focus:border-blue-400"
                                                            />
                                                        </div>

                                                    </div>
                                                )}
                                            </div>

                                            <div className={"overflow-x-auto"}>
                                                <table className={"w-full text-left min-w-[1090px] shadow-lg"}>
                                                    <thead className={"bg-gray-900/50"}>
                                                    <tr>
                                                        <th
                                                            className="
                                                        p-4
                                                        text-xs
                                                        font-semibold
                                                        text-gray-400
                                                        uppercase
                                                    "
                                                        >
                                                            UserName
                                                        </th>

                                                        <th
                                                            className="
                                                        p-4
                                                        text-xs
                                                        font-semibold
                                                        text-gray-400
                                                        uppercase
                                                    "
                                                        >
                                                            Project Name
                                                        </th>


                                                        <th
                                                            className="
                                                        p-4
                                                        text-xs
                                                        font-semibold
                                                        text-gray-400
                                                        uppercase
                                                    "
                                                        >
                                                            Project Number
                                                        </th>

                                                        <th
                                                            className="
                                                        p-4
                                                        text-xs
                                                        font-semibold
                                                        text-gray-400
                                                        uppercase
                                                    "
                                                        >
                                                            Reassign
                                                        </th>

                                                    </tr>
                                                    </thead>

                                                    {currentAssignedEmployees?.map((site) => (
                                                        <motion.tr
                                                            key={
                                                                site.id
                                                            }
                                                            initial={{
                                                                opacity: 0,
                                                            }}
                                                            animate={{
                                                                opacity: 1,
                                                            }}

                                                            className="
                                                            border-t
                                                            border-gray-800
                                                            hover:bg-gray-900/60
                                                            cursor-pointer
                                                            transition-colors
                                                        "
                                                        >
                                                            {/* Employee */}
                                                            <td className={"p-3"}>
                                                                <div className={"flex items-center gap-3"}>

                                                                    <p className={"font-medium text-gray-200"}>
                                                                        {site.userName}
                                                                    </p>
                                                                </div>
                                                            </td>

                                                            <td className={"p-3"}>
                                                                <div className={"flex items-center gap-2"}>

                                                                    <div className={"items-center"}>
                                                                        <p className={"text-gray-200 font-medium"}>
                                                                             {site.projectName}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            <td className={"p-4 text-gray-300"}>
                                                                <p className={"text-gray-200 font-medium"}>
                                                                    #{site.projectNumber}
                                                                </p>
                                                            </td>
                                                            <td className={"p-4 text-gray-300"}>
                                                                <button type={"button"}
                                                                onClick={() => onRemoveEmployeeFromProject(
                                                                    site.userId,
                                                                    site.projectId
                                                                )}
                                                                className={"rounded-lg p-2 text-red-500 transition hover:bg-red-500/20 hover:text-red-400"}
                                                                        title={"Remove employee from project"}
                                                                >
                                                                   <Trash2Icon size={20} />
                                                                </button>

                                                            </td>

                                                        </motion.tr>
                                                    ))}
                                                </table>

                                                {/* Desktop Pagination Button */}
                                                <div className={"flex justify-center space-x-2 mt-4"}>
                                                    <button
                                                        onClick={() => setCurrentPage((prev) => Math.max(prev -1, 1))}
                                                        className={"px-3 py-1 bg-gray-800 text-white rounded hover:bg-gray-600 "}
                                                        disabled={currentPage === 1}>
                                                        Prev
                                                    </button>

                                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                                        <button
                                                            key={page}
                                                            onClick={() => setCurrentPage(page)}
                                                            className={`px-3 py-1 rounded ${currentPage === page ? "bg-blue-500 text-white" : "bg-gray-700 text-white hover:bg-gray-600"}`}
                                                        >
                                                            {page}
                                                        </button>
                                                    ))}

                                                    <button onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                                            className={"px-3 py-1 bg-gray-800 text-white rounded hover:bg-gray-600"}
                                                            disabled={currentPage === totalPages}>
                                                        Next
                                                    </button>

                                                </div>

                                            </div>

                                        </div>

                                    </div>
                                </section>

                            </main>
                        </div>
                    </div>

                </motion.div>
            )}

        </>
    )
}